import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Subject } from "rxjs";
import { Router } from "@angular/router";
import { CoreConfigService } from "@core/services/config.service";
import { CoreTranslationService } from "@core/services/translation.service";
import { locale as english } from "app/main/pages/customer/cart-list-cust/i18n/en";
import { locale as indo } from "app/main/pages/customer/cart-list-cust/i18n/id";
import {
  LoginRequest,
  LoginResponse,
  LoginSuccessMessage,
} from "app/main/interfaces/auth";
import { User } from "app/main/models/user";
import { SharedUtilsService } from "app/main/utils/shared-utils.service";
import { Role } from "app/main/models/role";
import { PortalService } from "app/main/services/portal.service";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "app/main/services/auth.service";

@Component({
  selector: "app-auth-login-v2",
  templateUrl: "./auth-login-v2.component.html",
  styleUrls: ["./auth-login-v2.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class AuthLoginV2Component implements OnInit {
  //  Public
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = "";
  public passwordTextType: boolean;

  private _currentUserSubject: BehaviorSubject<User>;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _portalService: PortalService,
    private _coreTranslationService: CoreTranslationService,
    private _sharedUtilsService: SharedUtilsService,
    private _authService: AuthService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };

    // Initialize the form in constructor
    this.loginForm = this._formBuilder.group({
      username: ["", [Validators.required]],
      password: ["", [Validators.required]],
    });

    this._coreTranslationService.translate(english, indo);
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    // Login
    this.loading = true;

    let loginRequest: LoginRequest = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password,
    };

    // Call login service and handle response
    this._portalService.doLogin(loginRequest).subscribe({
      next: (response: LoginResponse) => {
        this.loading = false;

        // Handle successful login
        if (response.status === true) {
          const messageData = (response?.message as LoginSuccessMessage)?.data;
          const role: Role = this._sharedUtilsService.setRole(
            messageData?.privilege
          );

          let user: User = {
            id: messageData?.id,
            username: messageData?.username,
            email: messageData?.email,
            fullName: messageData?.fullname,
            label: messageData?.label,
            role: role,
            phone: messageData?.phone,
            token: messageData?.token,
          };

          // Store token or user data if needed
          localStorage.setItem("currentUser", JSON.stringify(user));

          this._authService["_currentUserSubject"].next(user);

          // Verify storage
          const storedUser = localStorage.getItem("currentUser");

          // Initialize BehaviorSubject if not done
          if (!this._currentUserSubject) {
            this._currentUserSubject = new BehaviorSubject<User>(user);
          } else {
            this._currentUserSubject.next(user);
          }

          // Redirect to home or intended page
          this._sharedUtilsService.goToHome(role);
        } else {
          // For failed login, message is a string
          this.error = response.message as string;
        }
      },
      error: (error) => {
        this.loading = false;

        // Properly extract error message
        if (error.error && error.error.message) {
          // If error.message is an object, stringify it, otherwise use as is
          if (typeof error.error.message === "object") {
            this.error = JSON.stringify(error.error.message);
          } else {
            this.error = error.error.message;
          }
        } else if (error.message) {
          this.error = error.message;
        } else {
          this.error = "Login failed. Please try again.";
        }

        console.error("Login error:", error);
      },
    });
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // get return url from route parameters or default to '/'
    this.returnUrl = this._route.snapshot.queryParams["returnUrl"] || "/";

    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });

    // Initialize BehaviorSubject if not done in constructor
    if (!this._currentUserSubject) {
      const currentUser = localStorage.getItem("currentUser");
      this._currentUserSubject = new BehaviorSubject<User>(
        currentUser ? JSON.parse(currentUser) : null
      );
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
